import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Briefcase, Eye, Check, X, MapPin, Calendar, Building2, Users, DollarSign, Search, Filter, FileText, ChevronLeft, ChevronRight, Phone, Mail, GraduationCap, Edit, Save } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { formatSalary } from '@/utils/api';
import ShareToWhatsAppButton from '@/components/ShareToWhatsAppButton';

const ExecutiveJobManagement = () => {
  const user = getUser();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [approvedFromDate, setApprovedFromDate] = useState('');
  const [approvedToDate, setApprovedToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 50;

  // Employer filter states
  const [employers, setEmployers] = useState([]);
  const [employerFilter, setEmployerFilter] = useState('all');
  const [employerSearch, setEmployerSearch] = useState('');
  const [showEmployerSuggestions, setShowEmployerSuggestions] = useState(false);
  const [filteredEmployers, setFilteredEmployers] = useState([]);
  const [selectedEmployerName, setSelectedEmployerName] = useState('');

  // Confirmation dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [pendingActionJobId, setPendingActionJobId] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, [currentPage, statusFilter, fromDate, toDate, approvedFromDate, approvedToDate, employerFilter]);

  useEffect(() => {
    fetchEmployersList();
  }, []);

  // Filter employers based on search input
  useEffect(() => {
    if (employerSearch.trim() && employers.length > 0) {
      const filtered = employers.filter(emp => 
        emp.company_name?.toLowerCase().includes(employerSearch.toLowerCase()) ||
        emp.name?.toLowerCase().includes(employerSearch.toLowerCase())
      );
      setFilteredEmployers(filtered.slice(0, 10));
      setShowEmployerSuggestions(true);
    } else {
      setFilteredEmployers([]);
      setShowEmployerSuggestions(false);
    }
  }, [employerSearch, employers]);

  const fetchEmployersList = async () => {
    try {
      // Get all employers (not filtered by approval status) for the dropdown
      const response = await api.get(`/executive/employers-list?executive_id=${user.id}&status=all`);
      setEmployers(response.data);
    } catch (error) {
      console.error('Failed to fetch employers list');
    }
  };

  const handleEmployerSelect = (emp) => {
    setEmployerFilter(emp.id);
    setSelectedEmployerName(emp.company_name || emp.name);
    setEmployerSearch('');
    setShowEmployerSuggestions(false);
  };

  const clearEmployerFilter = () => {
    setEmployerFilter('all');
    setSelectedEmployerName('');
    setEmployerSearch('');
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      let url = `/executive/jobs?executive_id=${user.id}&page=${currentPage}&limit=${limit}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (employerFilter !== 'all') url += `&employer_id=${employerFilter}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (approvedFromDate) url += `&approved_from_date=${approvedFromDate}`;
      if (approvedToDate) url += `&approved_to_date=${approvedToDate}`;
      
      const response = await api.get(url);
      setJobs(response.data.jobs || response.data.data || []);
      setTotalJobs(response.data.total || 0);
      setTotalPages(response.data.total_pages || response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    let url = `${process.env.REACT_APP_BACKEND_URL}/api/executive/jobs/export?executive_id=${user.id}&format=csv`;
    if (statusFilter !== 'all') url += `&status=${statusFilter}`;
    if (employerFilter !== 'all') url += `&employer_id=${employerFilter}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;
    if (approvedFromDate) url += `&approved_from_date=${approvedFromDate}`;
    if (approvedToDate) url += `&approved_to_date=${approvedToDate}`;
    window.open(url, '_blank');
    toast.success('Exporting jobs to CSV...');
  };

  const handleToggleStatus = async (job) => {
    try {
      await api.put(`/executive/jobs/${job.id}/toggle-status?executive_id=${user.id}`);
      toast.success(`Job ${job.active ? 'deactivated' : 'activated'} successfully`);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job status');
    }
  };

  // Approval with confirmation
  const openApproveDialog = (jobId) => {
    setPendingActionJobId(jobId);
    setShowApproveDialog(true);
  };

  const confirmApprove = async () => {
    setActionLoading(true);
    try {
      await api.put(`/executive/jobs/${pendingActionJobId}/approve?executive_id=${user.id}`);
      toast.success('Job approved successfully');
      fetchJobs();
      setShowApproveDialog(false);
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve job');
    } finally {
      setActionLoading(false);
      setPendingActionJobId(null);
    }
  };

  // Rejection with confirmation and notes
  const openRejectDialog = (jobId) => {
    setPendingActionJobId(jobId);
    setRejectionNotes('');
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!rejectionNotes.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`/executive/jobs/${pendingActionJobId}/reject?executive_id=${user.id}`, { 
        rejection_notes: rejectionNotes.trim() 
      });
      toast.success('Job rejected');
      fetchJobs();
      setShowRejectDialog(false);
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject job');
    } finally {
      setActionLoading(false);
      setPendingActionJobId(null);
      setRejectionNotes('');
    }
  };

  const viewJobDetails = (job) => {
    setSelectedJob(job);
    setIsEditing(false);
    setEditFormData(null);
    setShowDetailDialog(true);
  };

  // Edit functions
  const startEditing = () => {
    setEditFormData({
      job_title: selectedJob.job_title || '',
      job_type: selectedJob.job_type || 'Permanent',
      short_info: selectedJob.short_info || '',
      description: selectedJob.description || '',
      min_experience: selectedJob.min_experience || 0,
      max_experience: selectedJob.max_experience || 0,
      min_salary: selectedJob.min_salary || 0,
      max_salary: selectedJob.max_salary || 0,
      salary_type: selectedJob.salary_type || 'numeric',
      vacancies: selectedJob.vacancies || 1,
      last_date: selectedJob.last_date || '',
      contact_number: selectedJob.contact_number || '',
      email: selectedJob.email || '',
      min_education: selectedJob.min_education || '',
      gender: selectedJob.gender || [],
      locations: selectedJob.locations || [],
      skills: selectedJob.skills || [],
      min_age: selectedJob.min_age || '',
      max_age: selectedJob.max_age || '',
      publish_on: selectedJob.publish_on || ['job_portal']
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditFormData(null);
  };

  // Location states for edit
  const [locationSearchInput, setLocationSearchInput] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Location search function
  const searchLocations = async (query) => {
    if (query.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    try {
      const response = await api.get(`/location-master/locations/search?q=${encodeURIComponent(query)}&limit=10`);
      setLocationSuggestions(response.data || []);
      setShowLocationSuggestions(true);
    } catch (error) {
      console.error('Error searching locations:', error);
    }
  };

  const addLocation = (location) => {
    const newLoc = {
      state: location.state,
      district: location.district,
      city: location.city || location.district
    };
    const exists = editFormData.locations?.some(
      l => l.state === newLoc.state && l.district === newLoc.district
    );
    if (!exists) {
      setEditFormData({
        ...editFormData,
        locations: [...(editFormData.locations || []), newLoc]
      });
    }
    setLocationSearchInput('');
    setShowLocationSuggestions(false);
  };

  const removeLocation = (index) => {
    setEditFormData({
      ...editFormData,
      locations: editFormData.locations.filter((_, i) => i !== index)
    });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/executive/jobs/${selectedJob.id}/edit?executive_id=${user.id}`, editFormData);
      toast.success('Job updated successfully');
      setIsEditing(false);
      fetchJobs();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    }
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setStatusFilter('all');
    setSearchQuery('');
    setEmployerFilter('all');
    setSelectedEmployerName('');
    setEmployerSearch('');
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (job) => {
    if (job.status === 'pending') {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>;
    } else if (job.status === 'rejected') {
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
    } else if (job.active) {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>;
    } else {
      return <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">Inactive</Badge>;
    }
  };

  const getTypeBadge = (job) => {
    if (job.is_prime_employer_job) {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Prime</Badge>;
    }
    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Regular</Badge>;
  };

  // formatSalary is now imported from @/utils/api

  const filteredJobs = searchQuery 
    ? jobs.filter(j => 
        j.job_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        j.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : jobs;

  if (loading && currentPage === 1) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            Job Management
          </h1>
          <p className="text-slate-600">Manage jobs in your allocated districts ({totalJobs} total)</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleExportCSV} variant="outline" className="text-slate-600" data-testid="export-csv-btn">
            <FileText className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Job title or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="relative">
            <label className="block text-xs text-slate-500 mb-1">Employer</label>
            {selectedEmployerName ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-200 rounded-md">
                <Building2 className="w-4 h-4 text-teal-600" />
                <span className="flex-1 text-sm text-teal-800 truncate">{selectedEmployerName}</span>
                <button
                  type="button"
                  onClick={clearEmployerFilter}
                  className="text-teal-600 hover:text-teal-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search employer..."
                  value={employerSearch}
                  onChange={(e) => setEmployerSearch(e.target.value)}
                  onFocus={() => employerSearch && setShowEmployerSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowEmployerSuggestions(false), 200)}
                  className="pl-9"
                  data-testid="employer-search-input"
                />
              </div>
            )}
            {showEmployerSuggestions && filteredEmployers.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredEmployers.map((emp) => (
                  <div
                    key={emp.id}
                    className="px-3 py-2 cursor-pointer hover:bg-slate-100 flex items-center gap-2"
                    onMouseDown={() => handleEmployerSelect(emp)}
                  >
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{emp.company_name || 'N/A'}</p>
                      <p className="text-xs text-slate-500 truncate">{emp.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showEmployerSuggestions && employerSearch && filteredEmployers.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-3 text-center text-sm text-slate-500">
                No employers found
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Posted From</label>
            <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} data-testid="exec-from-date-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Posted To</label>
            <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} data-testid="exec-to-date-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Approved From</label>
            <Input type="date" value={approvedFromDate} onChange={(e) => { setApprovedFromDate(e.target.value); setCurrentPage(1); }} data-testid="exec-approved-from-date-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Approved To</label>
            <Input type="date" value={approvedToDate} onChange={(e) => { setApprovedToDate(e.target.value); setCurrentPage(1); }} data-testid="exec-approved-to-date-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Status</label>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">Clear</Button>
          </div>
        </div>
      </Card>

      {/* Jobs Table - Similar columns to Admin */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead>Serial No</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Approved Date</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Posted By</TableHead>
                <TableHead>Emp Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Experience</TableHead>
                <TableHead>Salary</TableHead>
                <TableHead>Skills</TableHead>
                <TableHead>Vacancies</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={16} className="text-center py-12">
                    <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No jobs found in your allocated districts</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredJobs.map((job, index) => (
                  <TableRow key={job.id} className="hover:bg-slate-50">
                    <TableCell className="text-center">{(currentPage - 1) * limit + index + 1}</TableCell>
                    <TableCell>
                      {job.serial_number ? (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono">{job.serial_number}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(job.created_at)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {job.approved_at ? (
                        <span className="text-emerald-700">{formatDate(job.approved_at)}</span>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900 max-w-[150px] truncate">{job.job_title}</div>
                      <div className="text-xs text-slate-500">{job.job_type}</div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[120px] truncate">{job.company_name || job.employer_name || 'N/A'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700 max-w-[100px] truncate" title={job.posted_by_name || 'N/A'}>
                        {job.posted_by_name || <span className="text-slate-400">N/A</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {job.employer_approval_status === 'approved' ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Approved</Badge>
                      ) : job.employer_approval_status === 'pending' ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Pending</Badge>
                      ) : job.employer_approval_status === 'rejected' ? (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Rejected</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-xs">Unknown</Badge>
                      )}
                    </TableCell>
                    <TableCell>{getTypeBadge(job)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[100px]">
                        {(job.locations || []).slice(0, 2).map((loc, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">
                            {loc.city && loc.city !== loc.district ? `${loc.city}, ${loc.district}` : (loc.city || loc.district)}
                          </span>
                        ))}
                        {job.locations?.length > 2 && <span className="text-xs text-slate-500">+{job.locations.length - 2}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{job.min_experience}-{job.max_experience} yrs</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{formatSalary(job)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[100px]">
                        {(job.skills || []).slice(0, 2).map((skill, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-xs truncate max-w-[50px]">{skill}</span>
                        ))}
                        {job.skills?.length > 2 && <span className="text-xs text-slate-500">+{job.skills.length - 2}</span>}
                        {(!job.skills || job.skills.length === 0) && <span className="text-slate-400 text-xs">N/A</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">{job.vacancies}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(job)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewJobDetails(job)} className="h-8 w-8 p-0" title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {job.status === 'pending' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700" 
                              onClick={() => viewJobDetails(job)}
                              title="Edit job"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed" 
                              onClick={() => openApproveDialog(job.id)}
                              disabled={job.employer_approval_status !== 'approved'}
                              title={job.employer_approval_status !== 'approved' ? 'Employer must be approved first' : 'Approve job'}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700" onClick={() => openRejectDialog(job.id)} title="Reject job">
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {job.status === 'approved' && (
                          <>
                            <Switch checked={job.active} onCheckedChange={() => handleToggleStatus(job)} />
                            <ShareToWhatsAppButton job={job} />
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-600">
            Page {currentPage} of {totalPages} ({totalJobs} jobs)
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <div className="flex gap-1">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Job Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={(open) => {
        setShowDetailDialog(open);
        if (!open) {
          setIsEditing(false);
          setEditFormData(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Edit Job' : 'Job Details'}</span>
              {selectedJob?.status === 'pending' && !isEditing && (
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedJob && !isEditing && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Job Title</p>
                  <p className="font-medium">{selectedJob.job_title}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Company</p>
                  <p className="font-medium">{selectedJob.company_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Job Type</p>
                  <p className="font-medium capitalize">{selectedJob.job_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Vacancies</p>
                  <p className="font-medium">{selectedJob.vacancies}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Experience</p>
                  <p className="font-medium">{selectedJob.min_experience} - {selectedJob.max_experience} years</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Salary Range</p>
                  <p className="font-medium">{formatSalary(selectedJob)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Contact Number</p>
                  <p className="font-medium">{selectedJob.contact_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedJob.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Posted By</p>
                  <p className="font-medium">{selectedJob.posted_by_name || 'N/A'}</p>
                </div>
                {selectedJob.min_education && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Required Education</p>
                    <p className="font-medium">{Array.isArray(selectedJob.min_education) ? selectedJob.min_education.join(', ') : selectedJob.min_education}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-sm text-slate-500">Locations</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedJob.locations?.map((loc, i) => (
                      <Badge key={i} variant="outline">
                        <MapPin className="w-3 h-3 mr-1" />
                        {loc.city && loc.city !== loc.district ? `${loc.city}, ${loc.district}` : loc.district}, {loc.state}
                      </Badge>
                    ))}
                  </div>
                </div>
                {selectedJob.skills && selectedJob.skills.length > 0 && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Skills</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedJob.skills.map((skill, i) => (
                        <Badge key={i} className="bg-purple-100 text-purple-700">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {selectedJob.description && (
                <div>
                  <p className="text-sm text-slate-500">Description</p>
                  <p className="text-sm mt-1 whitespace-pre-line">{selectedJob.description}</p>
                </div>
              )}
              
              {/* Action Buttons in Dialog */}
              {selectedJob.status === 'pending' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={() => openApproveDialog(selectedJob.id)} className="bg-green-600 hover:bg-green-700">
                    <Check className="w-4 h-4 mr-2" /> Approve Job
                  </Button>
                  <Button variant="destructive" onClick={() => openRejectDialog(selectedJob.id)}>
                    <X className="w-4 h-4 mr-2" /> Reject Job
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Edit Form */}
          {selectedJob && isEditing && editFormData && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Job Title</label>
                  <Input
                    value={editFormData.job_title}
                    onChange={(e) => setEditFormData({...editFormData, job_title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Job Type</label>
                  <Select value={editFormData.job_type} onValueChange={(v) => setEditFormData({...editFormData, job_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Trainee', 'On-Roll', 'Off-Roll', 'Permanent', 'Probation'].map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Vacancies</label>
                  <Input
                    type="number"
                    value={editFormData.vacancies}
                    onChange={(e) => setEditFormData({...editFormData, vacancies: parseInt(e.target.value) || 1})}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Experience (years)</label>
                  <Input
                    type="number"
                    value={editFormData.min_experience}
                    onChange={(e) => setEditFormData({...editFormData, min_experience: parseInt(e.target.value) || 0})}
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Experience (years)</label>
                  <Input
                    type="number"
                    value={editFormData.max_experience}
                    onChange={(e) => setEditFormData({...editFormData, max_experience: parseInt(e.target.value) || 0})}
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Salary</label>
                  <Input
                    type="number"
                    value={editFormData.min_salary}
                    onChange={(e) => setEditFormData({...editFormData, min_salary: parseInt(e.target.value) || 0})}
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Salary</label>
                  <Input
                    type="number"
                    value={editFormData.max_salary}
                    onChange={(e) => setEditFormData({...editFormData, max_salary: parseInt(e.target.value) || 0})}
                    min={0}
                    disabled={editFormData.salary_type !== 'numeric' && editFormData.salary_type !== 'numeric_range'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Salary Type</label>
                  <Select 
                    value={editFormData.salary_type || 'numeric'} 
                    onValueChange={(v) => setEditFormData({...editFormData, salary_type: v, max_salary: v === 'numeric' || v === 'numeric_range' ? editFormData.max_salary : 0})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="numeric">Numeric Range</SelectItem>
                      <SelectItem value="as_per_norms">As Per Norms</SelectItem>
                      <SelectItem value="as_per_experience">As Per Experience</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Number</label>
                  <Input
                    type="tel"
                    value={editFormData.contact_number}
                    onChange={(e) => {
                      // For prime employer jobs, allow comma-separated numbers
                      if (selectedJob?.is_prime_employer_job) {
                        setEditFormData({...editFormData, contact_number: e.target.value});
                      } else {
                        // For regular jobs, only allow digits up to 10
                        setEditFormData({...editFormData, contact_number: e.target.value.replace(/\D/g, '').slice(0, 10)});
                      }
                    }}
                    placeholder={selectedJob?.is_prime_employer_job ? "Multiple numbers (comma separated)" : "10-digit mobile number"}
                    maxLength={selectedJob?.is_prime_employer_job ? 100 : 10}
                  />
                  {selectedJob?.is_prime_employer_job && (
                    <p className="text-xs text-slate-500 mt-1">E.g., 9876543210, 8765432109</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Short Info</label>
                  <Input
                    value={editFormData.short_info}
                    onChange={(e) => setEditFormData({...editFormData, short_info: e.target.value})}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                    rows={4}
                  />
                </div>
                
                {/* Last Date & Course */}
                <div>
                  <label className="block text-sm font-medium mb-1">Last Date to Apply</label>
                  <Input
                    type="date"
                    value={editFormData.last_date}
                    onChange={(e) => setEditFormData({...editFormData, last_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Course/Subject</label>
                  <Input
                    value={editFormData.course_subject || ''}
                    onChange={(e) => setEditFormData({...editFormData, course_subject: e.target.value})}
                    placeholder="e.g., Computer Science, Mechanical Engineering"
                  />
                </div>

                {/* Education Qualification Multi-Select */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Education Qualification</label>
                  <div className="flex flex-wrap gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                    {['10th', '12th', 'ITI', 'Diploma', 'UG', 'PG', 'PhD', 'Any Degree'].map((edu) => (
                      <label key={edu} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Array.isArray(editFormData.min_education) ? editFormData.min_education.includes(edu) : editFormData.min_education === edu}
                          onChange={(e) => {
                            const currentEdu = Array.isArray(editFormData.min_education) ? editFormData.min_education : (editFormData.min_education ? [editFormData.min_education] : []);
                            if (e.target.checked) {
                              setEditFormData({...editFormData, min_education: [...currentEdu, edu]});
                            } else {
                              setEditFormData({...editFormData, min_education: currentEdu.filter(e => e !== edu)});
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        <span>{edu}</span>
                      </label>
                    ))}
                  </div>
                  {Array.isArray(editFormData.min_education) && editFormData.min_education.length > 0 && (
                    <p className="text-xs text-teal-600 mt-1">Selected: {editFormData.min_education.join(', ')}</p>
                  )}
                </div>

                {/* Skills */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Skills</label>
                  <Input
                    value={Array.isArray(editFormData.skills) ? editFormData.skills.join(', ') : editFormData.skills}
                    onChange={(e) => setEditFormData({...editFormData, skills: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                    placeholder="Enter skills separated by commas (e.g., Java, Python, SQL)"
                  />
                  <p className="text-xs text-slate-500 mt-1">Separate multiple skills with commas</p>
                </div>
                
                {/* Job Locations */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Job Locations</label>
                  <div className="relative">
                    <Input
                      value={locationSearchInput}
                      onChange={(e) => {
                        setLocationSearchInput(e.target.value);
                        searchLocations(e.target.value);
                      }}
                      placeholder="Search and add locations (e.g., Chennai, Coimbatore)"
                      onFocus={() => locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                    />
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {locationSuggestions.map((loc, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-2 hover:bg-teal-50 cursor-pointer text-sm flex items-center gap-2"
                            onClick={() => addLocation(loc)}
                          >
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span>{loc.state} / {loc.district}{loc.city && loc.city !== loc.district ? ` / ${loc.city}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {editFormData.locations?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.locations.map((loc, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 rounded-full text-xs">
                          <MapPin className="w-3 h-3" />
                          {loc.state} / {loc.district || loc.city}
                          <button
                            type="button"
                            onClick={() => removeLocation(idx)}
                            className="ml-1 text-teal-500 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {(!editFormData.locations || editFormData.locations.length === 0) && (
                    <p className="text-xs text-orange-500 mt-1">No locations added yet</p>
                  )}
                </div>

                {/* Gender & Age */}
                <div>
                  <label className="block text-sm font-medium mb-1">Gender</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Male', 'Female', 'Other'].map((g) => (
                      <label key={g} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          checked={editFormData.gender?.includes(g)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditFormData({...editFormData, gender: [...(editFormData.gender || []), g]});
                            } else {
                              setEditFormData({...editFormData, gender: editFormData.gender.filter(x => x !== g)});
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Age</label>
                  <Input
                    type="number"
                    value={editFormData.min_age}
                    onChange={(e) => setEditFormData({...editFormData, min_age: e.target.value ? parseInt(e.target.value) : ''})}
                    placeholder="e.g., 18"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Age</label>
                  <Input
                    type="number"
                    value={editFormData.max_age}
                    onChange={(e) => setEditFormData({...editFormData, max_age: e.target.value ? parseInt(e.target.value) : ''})}
                    placeholder="e.g., 35"
                  />
                </div>

                {/* Publishing Options - View Only for Executive */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Publish Job On (View Only)</label>
                  <div className="flex flex-wrap gap-4 p-3 border border-slate-200 rounded-lg bg-slate-100">
                    {[
                      { value: 'job_portal', label: 'Job Portal (VVVSI.com)' },
                      { value: 'whatsapp', label: 'WhatsApp' },
                      { value: 'youtube', label: 'YouTube' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={editFormData.publish_on?.includes(option.value)}
                          disabled
                          className="rounded border-slate-300 cursor-not-allowed opacity-70"
                        />
                        <span className="text-slate-600">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Publishing options can only be modified by Admin</p>
                </div>
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={handleSaveEdit} className="bg-teal-600 hover:bg-teal-700">
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </Button>
                <Button variant="outline" onClick={cancelEditing}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">Are you sure you want to approve this job posting?</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button onClick={confirmApprove} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              {actionLoading ? 'Approving...' : 'Yes, Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog with Notes */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">Please provide a reason for rejection:</p>
            <Textarea
              placeholder="Enter rejection reason (required)..."
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={actionLoading || !rejectionNotes.trim()}>
              {actionLoading ? 'Rejecting...' : 'Reject Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutiveJobManagement;
